import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

interface AIProcessingRecord {
    id: string;
    entity_type: "candidate" | "job" | "match";
    requested_at: string;
}

interface UseAIAnalysisProgressReturn {
    isProcessing: boolean;
    progress: number;
    timeRemaining: number;
    isCompleted: boolean;
    resetCompleted: () => void;
}

export const useAIAnalysisProgress = (
    entityId: string | undefined,
    entityType: "candidate" | "job" | "match",
    analysisType?: string
): UseAIAnalysisProgressReturn => {    
    
    const [isProcessing, setIsProcessing] = useState(false);

    const [progress, setProgress] = useState(0);
    const [timeRemaining, setTimeRemaining] = useState(0);
    const [isCompleted, setIsCompleted] = useState(false);
    
    // Use ref to track current progress value to avoid stale closures
    const progressRef = useRef(0);useEffect(() => {
        if (!entityId) return;

        let intervalId: NodeJS.Timeout | null = null;
        let channel: any = null;
        let processingStartTime: Date | null = null;

        const checkProcessingStatus = async () => {
            try {
                let query = supabase
                    .from("ai_processing")
                    .select("id, entity_type, entity_id, analysis, requested_at")
                    .eq("entity_id", entityId)
                    .eq("entity_type", entityType)
                    .eq("completed", false);
                
                if (analysisType) {
                    query = query.eq("analysis", analysisType);
                }

                const { data, error } = await query;

                if (error) {
                    console.error("Error checking AI processing status:", error);
                    return;
                }                
                
                if (!data || data.length === 0) {
                    // Processing completed - stop tracking
                    stopProgressTracking();
                    return;
                }

                // Processing is active - calculate progress
                const requestedAt = new Date(data[0].requested_at);
                const currentTime = new Date();
                const elapsedTime = (currentTime.getTime() - requestedAt.getTime()) / 1000; // in seconds
                const totalDuration = 180;
                const remainingTime = Math.max(0, totalDuration - elapsedTime);

                setTimeRemaining(remainingTime);                // If remaining time is below 10 seconds, stop the progress bar
                if (remainingTime <= 10) {
                    const finalProgress = 0.8;
                    // Only update if it's higher than current progress
                    if (finalProgress > progressRef.current) {
                        setProgress(finalProgress); // Stop at 80% to never reach 100%
                        progressRef.current = finalProgress;
                    }
                    return;
                }// Calculate progress with two phases for slower progress after 75 seconds
                let calculatedProgress: number;

                if (elapsedTime <= 75) {
                    // Phase 1: Linear progress from 0 to 60% for first 75 seconds with randomness
                    const baseProgress = (elapsedTime / 75) * 0.6;
                    
                    // Add randomness: ±20% speed variation, but ensure we don't go backwards or too fast
                    const randomFactor = 0.8 + (Math.random() * 0.4); // Random between 0.8 and 1.2
                    const randomizedProgress = baseProgress * randomFactor;
                    
                    // Ensure progress doesn't exceed what it should be at this time + small buffer
                    const maxAllowedProgress = Math.min(0.6, baseProgress * 1.3);
                    const tentativeProgress = Math.min(randomizedProgress, maxAllowedProgress);
                      // Ensure progress never decreases - use the maximum of current progress and new calculated progress
                    calculatedProgress = Math.max(progressRef.current, tentativeProgress);
                } else {
                    // Phase 2: Slower progress from 60% to 80% for remaining time (no randomness for consistency)
                    const phase2ElapsedTime = elapsedTime - 75;
                    const phase2Duration = totalDuration - 75 - 10; // 35 seconds (120 - 75 - 10)
                    const phase2Progress = Math.min(1, phase2ElapsedTime / phase2Duration) * 0.2; // 20% additional progress
                    calculatedProgress = 0.6 + phase2Progress;
                }                const finalProgress = Math.min(0.8, calculatedProgress);
                
                // Additional safeguard: only update progress if it's actually higher than current progress
                if (finalProgress > progressRef.current) {
                    setProgress(finalProgress);
                    progressRef.current = finalProgress;
                }
                setIsProcessing(true);

            } catch (error) {
                console.error("Error in checkProcessingStatus:", error);
            }
        };        
        
        const startProgressTracking = () => {

            setIsProcessing(true);
            setIsCompleted(false);
            setProgress(0);
            progressRef.current = 0;

            // Start interval for progress updates and DB checks
            intervalId = setInterval(checkProcessingStatus, 1000);

            // Initial check
            checkProcessingStatus();
        };
        
        const stopProgressTracking = () => {
            setIsProcessing(false);
            setProgress(0);
            progressRef.current = 0;
            setTimeRemaining(0);

            processingStartTime = null;

            // Set completed to true since stopProgressTracking is only called when processing actually completes
            setIsCompleted(true);

            if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
            }
        };

        const checkInitialProcessingStatus = async () => {
            try {
                let query = supabase
                    .from("ai_processing")
                    .select("id, entity_type, entity_id, analysis, requested_at")
                    .eq("entity_id", entityId)
                    .eq("entity_type", entityType)
                    .eq("completed", false);
                
                if (analysisType) {
                    query = query.eq("analysis", analysisType);
                }

                const { data, error } = await query;

                if (error) {
                    console.error("Error checking initial AI processing status:", error);
                    return;
                }

                if (data && data.length > 0) {
                    // Processing is already active
                    //console.log("AI processing already active:", data[0]);
                    startProgressTracking();
                }
            } catch (error) {
                console.error("Error in checkInitialProcessingStatus:", error);
            }
        };

        // Set up real-time channel to listen for AI processing broadcasts
        const channelId = `ai_processing:${entityId}`;
        channel = supabase.channel(channelId);

        channel
            .on(
                'broadcast',
                { event: 'ai_processing_requested' },
                (payload: any) => {
                    //console.log("AI processing started broadcast received:", payload);
                    const isMatchingEntity = payload.payload.entity_id === entityId && payload.payload.entity_type === entityType;
                    const isMatchingAnalysis = !analysisType || payload.payload.analysis === analysisType;
                    
                    if (isMatchingEntity && isMatchingAnalysis) {
                        startProgressTracking();
                    }
                }
            )
            .subscribe();

        // Initial check for existing processing
        checkInitialProcessingStatus();

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
            if (channel) {
                supabase.removeChannel(channel);
            }
        };    }, [entityId, entityType, analysisType]);

    const resetCompleted = () => {
        setIsCompleted(false);
    };

    return {
        isProcessing,
        progress,
        timeRemaining,
        isCompleted,
        resetCompleted
    };
};
